import { useEffect, useRef, useState, type RefObject } from "react";
import { ACI_COLORS, aciToHex } from "../scene/aciColorMap";
import { useT } from "../i18n";
import { TrueColorTab } from "./TrueColorTab";

/**
 * AutoCAD-style "Select Color" popup, mirroring AutoCAD's tabbed dialog.
 *
 * Tabs:
 *   - **Index Color (ACI)** — the standard 256-colour palette:
 *       - Top row: 9 standard colors (1..9) as large swatches
 *       - Main grid: extended palette (10..249) as small swatches (24 columns)
 *       - Bottom row: grey ramp (250..255)
 *     Clicking a swatch emits `onSelect(String(aci))`.
 *   - **True Color (RGB)** — R/G/B 0–255 fields + `#rrggbb` hex, kept in sync,
 *     a live preview, and OK → emits `onSelect("#rrggbb")`.
 *   - **Color Books** — DEFERRED (matches the S1/S2 bookColor-no-palette
 *     deferral); intentionally not stubbed with a fake palette.
 *
 * `onSelect` emits a single canonical STRING the property apply path already
 * accepts (`deserializeColor` → `coerceColor`): `String(aci)` for an index
 * selection, `"#rrggbb"` for a true colour. Clicking outside / Escape calls
 * `onClose()`.
 */

type ColorTab = "index" | "true";

type AciColorPickerProps = {
  /** Currently selected ACI index (highlighted with a border). */
  currentAci: number;
  /**
   * Called when the user commits a colour. Emits a canonical string the apply
   * path accepts: `String(aci)` (Index tab) or `"#rrggbb"` (True Color tab).
   */
  onSelect: (value: string) => void;
  /** Called to dismiss the popup (click outside / Escape). */
  onClose: () => void;
  /** Popup anchor position (CSS pixels — see {@link positionMode}). */
  anchorX: number;
  anchorY: number;
  /**
   * How the popup is positioned (round-2 FINDING D follow-up):
   *   - `"absolute"` (default) — `anchorX/anchorY` are relative to the nearest
   *     positioned ancestor (the offset parent). Used by PropertyGrid, whose
   *     wrapper is the offset parent and is NOT inside an `overflow:hidden` clip.
   *   - `"fixed"` — `anchorX/anchorY` are VIEWPORT coordinates (e.g. from
   *     `getBoundingClientRect`). Used when the popup is portal'd to
   *     `document.body` to ESCAPE an `overflow:hidden` ancestor (the ribbon
   *     swatch: `.ribbon-shell{overflow:hidden}` would otherwise clip it).
   */
  positionMode?: "absolute" | "fixed";
  /** Tab to open on first render (seeded from the cell's current value). */
  initialTab?: ColorTab;
  /** Seed hex for the True Color tab when the current value is a true colour. */
  initialHex?: string | null;
  /**
   * Anchor element whose clicks the outside-mousedown detector should IGNORE
   * (round-5 FINDING D). When the picker is portal'd and opened by a TOGGLE
   * button (the ribbon CECOLOR swatch), a click on that button would otherwise
   * count as an outside mousedown → `onClose()`, and the button's own onClick
   * would then re-open it — so a second click can never DISMISS. Passing the
   * anchor button's ref here makes the detector ignore it, so the button alone
   * owns the open/close toggle. Optional — callers that render the picker inline
   * (PropertyGrid) don't need it and keep the current close-on-outside behaviour.
   */
  ignoreRef?: RefObject<HTMLElement | null>;
  /**
   * CECOLOR round-6 FINDING D — render symbolic ByLayer / ByBlock action buttons
   * above the tab bar. The generic ACI picker (Index 1..255 + True Color) has no
   * way to return a colour to the by-reference union members, but the CECOLOR
   * swatch is the advertised current-colour control and AutoCAD's "Select Color"
   * dialog offers ByLayer / ByBlock there. Opt-in (default off) so the inline
   * PropertyGrid / CLI picker call sites — which own their own ByLayer/ByBlock
   * affordances — are NOT regressed. When set, the buttons emit
   * `onSelect("ByLayer")` / `onSelect("ByBlock")`, which the caller's
   * `coerceColor` resolves to `{kind:"byLayer"}` / `{kind:"byBlock"}`.
   */
  showByReference?: boolean;
  /**
   * The current by-reference selection ("byLayer" | "byBlock"), used to
   * highlight the active button when {@link showByReference} is set. Undefined
   * for an index / true-colour current value (no by-reference button active).
   */
  currentReference?: "byLayer" | "byBlock";
};

const STANDARD_COLORS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const EXTENDED_START = 10;
const EXTENDED_END = 249;
const GREY_RAMP = [250, 251, 252, 253, 254, 255];

/**
 * One swatch button — the single source of truth shared by all three Index-tab
 * sections (standard / extended / grey ramp). They differ only by CSS class and
 * the resolved colour; the button shape (selected border, title, emit
 * `String(aci)`) is identical, so it lives here once (jscpd one-truth).
 */
function AciSwatch({
  aci,
  color,
  className,
  selected,
  onSelect,
}: {
  aci: number;
  color: string;
  className: string;
  selected: boolean;
  onSelect: (value: string) => void;
}) {
  return (
    <button
      type="button"
      className={`${className} ${selected ? "is-selected" : ""}`}
      style={{ backgroundColor: color }}
      title={`${aci}`}
      onClick={() => onSelect(String(aci))}
    />
  );
}

export function AciColorPicker({
  currentAci,
  onSelect,
  onClose,
  anchorX,
  anchorY,
  positionMode = "absolute",
  initialTab = "index",
  initialHex = null,
  ignoreRef,
  showByReference = false,
  currentReference,
}: AciColorPickerProps) {
  const t = useT();
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [tab, setTab] = useState<ColorTab>(initialTab);

  // Close on outside click
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      const target = e.target as Node;
      // round-5 FINDING D — ignore the anchor toggle button (when provided) so a
      // click on it does NOT count as an outside dismiss that the button's own
      // onClick would immediately re-open; the button alone owns the toggle.
      if (ignoreRef?.current && ignoreRef.current.contains(target)) {
        return;
      }
      if (popupRef.current && !popupRef.current.contains(target)) {
        onClose();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose, ignoreRef]);

  const extendedColors: number[] = [];
  for (let i = EXTENDED_START; i <= EXTENDED_END; i++) {
    extendedColors.push(i);
  }

  // Seed the True Color tab from the CURRENT colour: an explicit true-colour
  // (`initialHex`), else the current ACI's RGB equivalent (`aciToHex`) so
  // switching an Index colour → True Color preserves it instead of resetting to
  // #000000 (@codex P2). Falls back to black only when there is no current ACI.
  const effectiveHex = initialHex ?? aciToHex(currentAci) ?? "#000000";

  return (
    <div
      ref={popupRef}
      className="aci-color-picker-popup"
      style={{
        position: positionMode,
        left: `${anchorX}px`,
        top: `${anchorY}px`,
      }}
      data-testid="aci-color-picker"
    >
      <div className="aci-color-picker-title">{t("color_picker.title")}</div>

      {/* CECOLOR round-6 FINDING D — symbolic ByLayer / ByBlock actions (CECOLOR
          swatch only, opt-in via showByReference). AutoCAD's "Select Color"
          dialog offers these for the current colour; the generic index/true
          tabs cannot express the by-reference union. Emits the canonical
          "ByLayer"/"ByBlock" strings the caller's coerceColor resolves. */}
      {showByReference && (
        <div
          className="aci-color-picker-byref"
          data-testid="aci-color-picker-byref"
        >
          <button
            type="button"
            className={`aci-color-byref-button ${
              currentReference === "byLayer" ? "is-selected" : ""
            }`}
            data-testid="aci-color-byref-bylayer"
            onClick={() => onSelect("ByLayer")}
          >
            {t("cecolor.swatch.bylayer")}
          </button>
          <button
            type="button"
            className={`aci-color-byref-button ${
              currentReference === "byBlock" ? "is-selected" : ""
            }`}
            data-testid="aci-color-byref-byblock"
            onClick={() => onSelect("ByBlock")}
          >
            {t("cecolor.swatch.byblock")}
          </button>
        </div>
      )}

      {/* Tab bar — AutoCAD "Select Color": Index Color | True Color */}
      <div className="color-picker-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "index"}
          className={`color-picker-tab ${tab === "index" ? "is-active" : ""}`}
          data-testid="color-picker-tab-index"
          onClick={() => setTab("index")}
        >
          {t("color_picker.tab.index")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "true"}
          className={`color-picker-tab ${tab === "true" ? "is-active" : ""}`}
          data-testid="color-picker-tab-true"
          onClick={() => setTab("true")}
        >
          {t("color_picker.tab.true")}
        </button>
      </div>

      {/* Index grid is heavy (256 swatches) → mount only when active. */}
      {tab === "index" && (
        <>
          {/* Standard colors (1..9) */}
          <div className="aci-color-picker-section-label">
            {t("color_picker.standard")}
          </div>
          <div className="aci-color-picker-standard">
            {STANDARD_COLORS.map((aci) => (
              <AciSwatch
                key={aci}
                aci={aci}
                color={aciToHex(aci) ?? "#000"}
                className="aci-color-swatch-standard"
                selected={currentAci === aci}
                onSelect={onSelect}
              />
            ))}
          </div>

          {/* Extended palette (10..249) */}
          <div className="aci-color-picker-grid">
            {extendedColors.map((aci) => (
              <AciSwatch
                key={aci}
                aci={aci}
                color={ACI_COLORS[aci] ?? "#000"}
                className="aci-color-swatch"
                selected={currentAci === aci}
                onSelect={onSelect}
              />
            ))}
          </div>

          {/* Grey ramp (250..255) */}
          <div className="aci-color-picker-section-label">
            {t("color_picker.grey_ramp")}
          </div>
          <div className="aci-color-picker-grey">
            {GREY_RAMP.map((aci) => (
              <AciSwatch
                key={aci}
                aci={aci}
                color={ACI_COLORS[aci] ?? "#000"}
                className="aci-color-swatch-grey"
                selected={currentAci === aci}
                onSelect={onSelect}
              />
            ))}
          </div>
        </>
      )}
      {/* True Color tab stays MOUNTED across tab switches (hidden when inactive)
          so in-progress RGB/hex edits survive an Index round-trip before OK
          (@codex P2 — child draft state must not reset on remount). */}
      <div style={{ display: tab === "true" ? "block" : "none" }}>
        <TrueColorTab initialHex={effectiveHex} onSelect={onSelect} />
      </div>
    </div>
  );
}
